/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import {
    Workplan,
    WorkplanAttachment,
    WorkplanStateHistory,
    WorkplanSummary,
    WorkplanVersion,
} from '@/wpt-lib/interfaces/Workplan';
import { createUnprovisionedContext } from '@/wpt-lib/utils/contextHelpers';
import { useContext } from 'react';
import { User } from '@/wpt-lib/interfaces/User';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { BookedLeave, LeaveFormResponse } from '@/wpt-lib/interfaces/Leave';
import { PersonalGoalFormResponse } from '@/wpt-lib/interfaces/PersonalGoal';
import { ActivityFocusArea, ActivityFormResponse } from '@/wpt-lib/interfaces/Activity';
import { FormLabels } from '@/wpt-lib/interfaces/FormLabels';
import { label } from '@/wpt-lib/utils/label';
import { BaseFormResponse } from '@/wpt-lib/interfaces/baseFormResponse';
import { WorkplanComment } from '@/wpt-lib/interfaces/WorkplanComment';
import { HealthAndSafetyTrainingFormResponse } from '@/wpt-lib/interfaces/HealthAndSafetyTraining';
import { parseFormData } from '@/wpt-lib/utils/formData';
import { FormChange } from '@/wpt-lib/interfaces/EntityChange';
import { ConsiderationFormResponse } from '@/wpt-lib/interfaces/Consideration';

export interface PDFData {
    user: User;
    assignment: Assignment;
    workplan: Workplan;
    version: WorkplanVersion;
    comments: WorkplanComment[];
    stateHistory: WorkplanStateHistory[];
    summary: WorkplanSummary;
    leaves: Omit<LeaveFormResponse, keyof FormChange>[];
    bookedLeaves: BookedLeave[];
    goals: Omit<PersonalGoalFormResponse, keyof FormChange>[];
    activities: Omit<ActivityFormResponse, keyof FormChange>[];
    training: Omit<HealthAndSafetyTrainingFormResponse, keyof FormChange>[];
    labels: FormLabels;
    considerations: Omit<ConsiderationFormResponse, keyof FormChange>[];
    workplanAttachments: WorkplanAttachment[];
}

export const DataContext = createUnprovisionedContext<PDFData>('DataContext');

function useData(): PDFData {
    return useContext(DataContext);
}

export default useData;

export type ParsedWorkplanForm = Omit<BaseFormResponse, keyof FormChange> & {
    data: {
        [item: string]: string | undefined | object;
    };
};

export function useGoals(): ParsedWorkplanForm[] {
    return useData().goals.map((goal) => ({
        ...goal,
        data: parseFormData(goal.workplanItem.formData),
    }));
}

export function useTraining(): ParsedWorkplanForm[] {
    return useData().training.map((goal) => ({
        ...goal,
        data: parseFormData(goal.workplanItem.formData),
    }));
}

export function useActivitiesFocusArea(area: ActivityFocusArea): ParsedWorkplanForm[] {
    const data = useData();

    return data.activities
        .map((activity) => ({
            ...activity,
            data: parseFormData(activity.workplanItem.formData),
        }))
        .filter((activity) => activity.data.academicFocusArea === area);
}

export function useLabels(property: string): FormLabels['property'] {
    const labels = useData().labels;
    return property in labels ? labels[property] : {};
}

export function useLabel(property: string, field: string): string {
    return label(useLabels(property), field);
}

export function useConsideration(): ParsedWorkplanForm[] {
    return useData().considerations.map((consideration) => ({
        ...consideration,
        data: parseFormData(consideration.workplanItem.formData),
    }));
}
